import React, { useEffect, useState, useMemo } from 'react'
import { useNavigate, Link as RouterLink } from 'react-router-dom'
import { Helmet } from 'react-helmet-async'
import {
  Autocomplete,
  Box,
  Button,
  Chip,
  List,
  ListItemButton,
  ListItemText,
  ListItemIcon,
  TextField,
  Typography,
  CircularProgress,
  Alert,
} from '@mui/material'
import {
  Business as BusinessIcon,
  ChevronRight as ChevronRightIcon,
  AdminPanelSettings as AdminPanelIcon,
} from '@mui/icons-material'
import { canAccessAdminPanel } from '../../services/auth'
import StandaloneCardLayout from '../../components/layouts/StandaloneCardLayout'
import AuthPageFooter from '../../components/auth/AuthPageFooter'
import { secondaryTealButtonSx } from '../../components/auth/authButtonStyles'
import { apiClient } from '../../services/api'
import { generateInspectorUrl } from '../../utils/urls'
import { useAuth } from '../../contexts/AuthContext'
import { getCountryChipConfig } from '../../utils/countryChips'

interface AccessibleFranchise {
  id: number
  fp_code: string
  franchise_name: string
  country: string
  state: string
  fp_status: string
  system_status: string
}

const ENHANCED_VIEW_THRESHOLD = 5

const getStatusColor = (status: string): 'success' | 'info' | 'error' | 'default' => {
  switch (status) {
    case 'Active': return 'success'
    case 'Onboarding': return 'info'
    case 'Terminated': return 'error'
    case 'Test': default: return 'default'
  }
}

const highlightText = (text: string, searchTerm: string) => {
  if (!searchTerm.trim()) return text
  const regex = new RegExp(`(${searchTerm.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi')
  const parts = text.split(regex)
  return parts.map((part, index) =>
    regex.test(part) ? (
      <span key={index} style={{ backgroundColor: '#ffeb3b', fontWeight: 'bold' }}>
        {part}
      </span>
    ) : (
      part
    )
  )
}

const ChooseFranchise: React.FC = () => {
  const navigate = useNavigate()
  const { user, isAuthenticated, loading: authLoading } = useAuth()
  const [franchises, setFranchises] = useState<AccessibleFranchise[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [searchValue, setSearchValue] = useState('')
  const [dropdownOpen, setDropdownOpen] = useState(false)

  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      window.location.href = '/login'
    }
  }, [authLoading, isAuthenticated])

  useEffect(() => {
    if (!isAuthenticated) return
    const loadFranchises = async () => {
      try {
        const response = await apiClient.get('/user/accessible-franchises')
        const fps = response.data
        if (fps.length === 1) {
          navigate(generateInspectorUrl(fps[0].fp_code, { id: fps[0].id }), { replace: true })
          return
        }
        const sorted = [...fps].sort((a: AccessibleFranchise, b: AccessibleFranchise) => {
          const aActive = a.fp_status === 'Active'
          const bActive = b.fp_status === 'Active'
          if (aActive && !bActive) return -1
          if (!aActive && bActive) return 1
          return a.franchise_name.localeCompare(b.franchise_name)
        })
        setFranchises(sorted)
      } catch (err: any) {
        setError(err.response?.data?.detail || err.message || 'Failed to load franchises')
      } finally {
        setLoading(false)
      }
    }
    loadFranchises()
  }, [navigate, isAuthenticated])

  const filteredFranchises = useMemo(() => {
    if (!searchValue.trim()) return franchises
    const q = searchValue.toLowerCase()
    return franchises.filter(fp =>
      fp.franchise_name.toLowerCase().includes(q) ||
      fp.fp_code.toLowerCase().includes(q)
    )
  }, [franchises, searchValue])

  const useEnhancedView = franchises.length > ENHANCED_VIEW_THRESHOLD

  const renderOption = (props: any, option: AccessibleFranchise) => {
    const normalizedCountry = option.country === 'USA' ? 'US' : option.country
    const countryConfig = getCountryChipConfig(normalizedCountry)
    const isActive = option.fp_status === 'Active'

    return (
      <Box component="li" {...props} key={option.fp_code}>
        <Box sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          py: 0.5,
          width: '100%',
          opacity: isActive ? 1 : 0.5
        }}>
          <Chip
            label={normalizedCountry}
            size="small"
            variant="outlined"
            sx={{
              height: 22,
              fontSize: '0.75rem',
              minWidth: 32,
              backgroundColor: countryConfig.backgroundColor,
              borderColor: countryConfig.borderColor,
              '& .MuiChip-label': { fontWeight: 500 }
            }}
          />
          <Typography variant="body2" fontWeight={500} noWrap sx={{ flex: 1 }}>
            {highlightText(option.franchise_name, searchValue)}
          </Typography>
          {option.fp_status && (
            <Chip
              label={option.fp_status}
              size="small"
              color={getStatusColor(option.fp_status)}
              sx={{ height: 22, fontSize: '0.75rem' }}
            />
          )}
        </Box>
      </Box>
    )
  }

  if (authLoading || !isAuthenticated) {
    return null
  }

  return (
    <>
    <Helmet>
      <title>Choose Franchise Partner | Inspector Tool</title>
    </Helmet>
    <StandaloneCardLayout
      headerTitle="Choose Franchise Partner"
      headerIcon={<BusinessIcon sx={{ fontSize: 48 }} />}
    >
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : error ? (
        <Alert severity="error">{error}</Alert>
      ) : franchises.length === 0 ? (
        <Alert severity="info">
          No franchise partners are available. Please contact your administrator.
        </Alert>
      ) : useEnhancedView ? (
        <Box>
          <Autocomplete
            open={dropdownOpen}
            onOpen={() => setDropdownOpen(true)}
            onClose={() => setDropdownOpen(false)}
            options={filteredFranchises}
            getOptionLabel={(option) => {
              const country = option.country === 'USA' ? 'US' : option.country
              return `${country} - ${option.franchise_name}`
            }}
            renderOption={renderOption}
            onChange={(_event, selectedOption) => {
              if (selectedOption) navigate(generateInspectorUrl(selectedOption.fp_code, { id: selectedOption.id }))
            }}
            inputValue={searchValue}
            onInputChange={(_event, newValue) => setSearchValue(newValue)}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder="Search by name or code..."
                variant="outlined"
                fullWidth
                autoFocus
              />
            )}
            noOptionsText="No franchise partners found"
            componentsProps={{
              popper: {
                placement: 'bottom',
                modifiers: [{ name: 'flip', enabled: false }]
              }
            }}
            sx={{
              '& .MuiAutocomplete-listbox': {
                maxHeight: 400
              }
            }}
          />
        </Box>
      ) : (
        <List disablePadding>
          {franchises.map((fp) => (
            <ListItemButton
              key={fp.fp_code}
              component={RouterLink}
              to={generateInspectorUrl(fp.fp_code, { id: fp.id })}
              sx={{
                borderBottom: '1px solid',
                borderColor: 'divider',
                '&:hover': { bgcolor: 'action.hover' },
              }}
            >
              <ListItemIcon sx={{ minWidth: 40 }}>
                <BusinessIcon color="action" />
              </ListItemIcon>
              <ListItemText
                primary={fp.franchise_name}
                primaryTypographyProps={{ variant: 'body1', fontWeight: 600 }}
                secondary={[
                  fp.fp_code.toUpperCase(),
                  [fp.state, fp.country === 'USA' ? 'US' : fp.country].filter(Boolean).join(', '),
                ].filter(Boolean).join(' · ')}
                secondaryTypographyProps={{ variant: 'body2', fontWeight: 600, color: 'text.secondary' }}
              />
              <ChevronRightIcon color="action" />
            </ListItemButton>
          ))}
        </List>
      )}
      {user && canAccessAdminPanel(user) && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3, mb: 2 }}>
          <Button
            component={RouterLink}
            to="/admin"
            variant="outlined"
            startIcon={<AdminPanelIcon />}
            sx={[
              secondaryTealButtonSx,
              // Keep this a compact, link-style secondary action (not a
              // full-height 52px primary) - it sits under the FP list.
              { minHeight: 'auto', fontSize: '0.9rem', px: 3, py: 1 },
            ]}
          >
            Admin Panel
          </Button>
        </Box>
      )}
      {user && <AuthPageFooter user={user} />}
    </StandaloneCardLayout>
    </>
  )
}

export default ChooseFranchise
